import React from 'react';
import { Typography, Empty } from 'antd';
import { PermissionToggleChip } from './PermissionToggleChip';
import type { GroupedResource, PermissionLevel } from '../../pages/Admin/components/hooks/useRolePermissionsPage';
import { getResourceDescription } from './permissionCategoryHelpers';

const { Text } = Typography;

interface ModulePermissionsListProps {
  resources: GroupedResource[];
  pendingResources: Set<string>;
  onSelect: (resourceId: string, level: PermissionLevel) => void;
}

const INDENT_PER_LEVEL = 28;

export const ModulePermissionsList: React.FC<ModulePermissionsListProps> = ({
  resources,
  pendingResources,
  onSelect,
}) => {
  if (resources.length === 0) {
    return <Empty description="Нет ресурсов" />;
  }

  return (
    <div
      style={{
        borderRadius: 10,
        border: '1px solid #ececec',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 16px',
          background: 'var(--color-bg-primary)',
          borderBottom: '1px solid var(--color-border-primary)',
        }}
      >
        <div style={{ flex: 1 }}>
          <Text
            type="secondary"
            style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}
          >
            Ресурс
          </Text>
        </div>
        <Text
          type="secondary"
          style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, minWidth: 100, textAlign: 'center' }}
        >
          Доступ
        </Text>
      </div>

      {/* Resource rows */}
      {resources.map((item) => {
        const description = getResourceDescription(item.resource.value);
        const isModule = item.level === 0;
        const isStage = item.level === 1;
        const isTable = item.level === 2;
        const indent = item.level * INDENT_PER_LEVEL;
        const isPending = pendingResources.has(item.resource.id);

        return (
          <div
            key={item.resource.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: `10px 16px 10px ${16 + indent}px`,
              borderBottom: '1px solid var(--color-border-primary)',
              background: isTable ? 'var(--color-bg-primary)' : 'var(--color-bg-surface)',
              transition: 'background 0.15s',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'var(--color-bg-primary)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = isTable
                ? 'var(--color-bg-primary)'
                : 'var(--color-bg-surface)';
            }}
          >
            {/* Tree connector */}
            {(isStage || isTable) && (
              <span
                style={{
                  color: 'var(--color-border-primary)',
                  fontSize: 11,
                  lineHeight: 1,
                  userSelect: 'none',
                  position: 'absolute',
                  left: 16 + indent - 16,
                }}
              >
                └
              </span>
            )}

            {/* Resource info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text
                strong={isModule || isStage}
                style={{
                  fontSize: isModule ? 14 : isStage ? 13 : 12.5,
                  color: isModule ? 'var(--color-text-primary)' : isStage ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  lineHeight: 1.3,
                }}
              >
                {item.resource.name}
              </Text>
              {description && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    display: 'block',
                    marginTop: 1,
                    lineHeight: 1.3,
                  }}
                >
                  {description}
                </Text>
              )}
            </div>

            {/* Toggle chip */}
            <div style={{ flexShrink: 0 }}>
              <PermissionToggleChip
                level={item.currentLevel}
                onSelect={(level) => onSelect(item.resource.id, level)}
                loading={isPending}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
